import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Paper, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Divider, AppBar, Toolbar,
  CircularProgress, Alert, Snackbar
} from '@mui/material';
import { db, auth } from './services/firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import DeleteIcon from '@mui/icons-material/Delete';
import LogoHeader from './LogoHeader';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';

export default function AdminPage() {
  const [promoText, setPromoText] = useState('');
  const [promoImageUrl, setPromoImageUrl] = useState('');
  const [promoList, setPromoList] = useState<any[]>([]);
  const [clienti, setClienti] = useState<any[]>([]);
  const [editingPromoId, setEditingPromoId] = useState<string | null>(null);
  const [editingPromoText, setEditingPromoText] = useState('');
  const [editingPromoImage, setEditingPromoImage] = useState('');
  const [volantinoName, setVolantinoName] = useState('');
  const [volantinoUrl, setVolantinoUrl] = useState('');
  const [volantiniList, setVolantiniList] = useState<any[]>([]);
  const [editingVolantinoId, setEditingVolantinoId] = useState<string | null>(null);
  const [editingVolantino, setEditingVolantino] = useState<any>({});
  const [searchCliente, setSearchCliente] = useState('');
  const [editingClienteId, setEditingClienteId] = useState<string | null>(null);
  const [editingCliente, setEditingCliente] = useState<any>({});
  const [searchWin, setSearchWin] = useState('');
  
  // Stati per l'upload di file
  const [selectedPromoFile, setSelectedPromoFile] = useState<File | null>(null);
  const [selectedVolantinoFile, setSelectedVolantinoFile] = useState<File | null>(null);
  const [uploadingPromo, setUploadingPromo] = useState(false);
  const [uploadingVolantino, setUploadingVolantino] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{message: string, severity: 'success' | 'error'} | null>(null);
  
  // Riferimenti agli input file per reset
  const promoFileInputRef = useRef<HTMLInputElement>(null);
  const volantinoFileInputRef = useRef<HTMLInputElement>(null);

  // --- Stati per i premi gratta e vinci ---
  const [scratchPrizes, setScratchPrizes] = useState<any[]>([]);
  const [prizeName, setPrizeName] = useState('');
  const [prizeImageUrl, setPrizeImageUrl] = useState('');
  const [selectedPrizeFile, setSelectedPrizeFile] = useState<File | null>(null);
  const [uploadingPrize, setUploadingPrize] = useState(false);
  const prizeFileInputRef = useRef<HTMLInputElement>(null);
  const [editingPrizeId, setEditingPrizeId] = useState<string | null>(null);
  const [editingPrize, setEditingPrize] = useState<any>({});

  // --- Stati ---
  const [pendingWins, setPendingWins] = useState<any[]>([]);

  const clientiMap = React.useMemo(() => {
    const map: Record<string, any> = {};
    clienti.forEach(c => { map[c.id] = c; });
    return map;
  }, [clienti]);

  useEffect(() => {
    getDocs(collection(db, 'promo')).then(snapshot => {
      setPromoList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    getDocs(collection(db, 'utenti')).then(snapshot => {
      setClienti(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    getDocs(collection(db, 'volantini')).then(snapshot => {
      setVolantiniList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    getDocs(collection(db, 'scratch_prizes')).then(snapshot => {
      setScratchPrizes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
  }, []);

  // --- Carica vincite non ritirate ---
  useEffect(() => {
    const fetchWins = async () => {
      const q = query(collection(db, 'scratch_coupons'), where('ritirato', '==', false));
      const snap = await getDocs(q);
      setPendingWins(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchWins();
  }, []);

  // Funzione generale per upload file
  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('https://smoke.gelarredi.it/upload/upload.php', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`Errore di rete: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.success) {
        setAlertMessage({
          message: 'File caricato con successo',
          severity: 'success'
        });
        return result.url;
      } else {
        throw new Error(result.error || 'Upload fallito');
      }
    } catch (error: any) {
      setAlertMessage({
        message: `Errore: ${error.message}`,
        severity: 'error'
      });
      throw error;
    }
  };

  // Gestione file promo
  const handlePromoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedPromoFile(e.target.files[0]);
    }
  };

  const handleAddPromoWithUpload = async () => {
    if (!promoText.trim()) return;
    
    try {
      setUploadingPromo(true);
      let imageUrl = '';
      
      if (selectedPromoFile) {
        imageUrl = await uploadFile(selectedPromoFile);
      } else if (promoImageUrl.trim()) {
        imageUrl = promoImageUrl;
      }
      
      // Salva in Firestore
      const docRef = await addDoc(collection(db, 'promo'), { 
        testo: promoText, 
        image: imageUrl 
      });
      
      // Aggiorna la lista
      setPromoList([...promoList, { 
        id: docRef.id, 
        testo: promoText, 
        image: imageUrl 
      }]);
      
      // Reset campi
      setPromoText('');
      setPromoImageUrl('');
      setSelectedPromoFile(null);
      if (promoFileInputRef.current) promoFileInputRef.current.value = '';
    } catch (error) {
      console.error('Errore nell\'aggiunta della promo:', error);
    } finally {
      setUploadingPromo(false);
    }
  };

  // Gestione file volantini
  const handleVolantinoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedVolantinoFile(e.target.files[0]);
    }
  };

  const handleAddVolantinoWithUpload = async () => {
    if (!volantinoName.trim()) return;
    
    try {
      setUploadingVolantino(true);
      let fileUrl = '';
      
      if (selectedVolantinoFile) {
        fileUrl = await uploadFile(selectedVolantinoFile);
      } else if (volantinoUrl.trim()) {
        fileUrl = volantinoUrl;
      }
      
      // Salva in Firestore
      const docRef = await addDoc(collection(db, 'volantini'), { 
        nome: volantinoName, 
        url: fileUrl
      });
      
      // Aggiorna la lista
      setVolantiniList([...volantiniList, { 
        id: docRef.id, 
        nome: volantinoName, 
        url: fileUrl 
      }]);
      
      // Reset campi
      setVolantinoName('');
      setVolantinoUrl('');
      setSelectedVolantinoFile(null);
      if (volantinoFileInputRef.current) volantinoFileInputRef.current.value = '';
    } catch (error) {
      console.error('Errore nell\'aggiunta del volantino:', error);
    } finally {
      setUploadingVolantino(false);
    }
  };

  // Gestione file premio
  const handlePrizeFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedPrizeFile(e.target.files[0]);
    }
  };

  const handleAddPrizeWithUpload = async () => {
    if (!prizeName.trim()) return;
    try {
      setUploadingPrize(true);
      let imageUrl = '';
      if (selectedPrizeFile) {
        imageUrl = await uploadFile(selectedPrizeFile);
      } else if (prizeImageUrl.trim()) {
        imageUrl = prizeImageUrl;
      }
      const docRef = await addDoc(collection(db, 'scratch_prizes'), {
        nome: prizeName,
        img: imageUrl
      });
      setScratchPrizes([...scratchPrizes, { id: docRef.id, nome: prizeName, img: imageUrl }]);
      setPrizeName('');
      setPrizeImageUrl('');
      setSelectedPrizeFile(null);
      if (prizeFileInputRef.current) prizeFileInputRef.current.value = '';
    } catch (error) {
      setAlertMessage({ message: 'Errore aggiunta premio', severity: 'error' });
    } finally {
      setUploadingPrize(false);
    }
  };

  const handleDeletePrize = async (id: string) => {
    await deleteDoc(doc(db, 'scratch_prizes', id));
    setScratchPrizes(scratchPrizes.filter(p => p.id !== id));
  };

  const handleEditPrize = (prize: any) => {
    setEditingPrizeId(prize.id);
    setEditingPrize({ ...prize });
  };

  const handleSavePrize = async () => {
    if (!editingPrizeId) return;
    await updateDoc(doc(db, 'scratch_prizes', editingPrizeId), editingPrize);
    setScratchPrizes(scratchPrizes.map(p =>
      p.id === editingPrizeId ? { ...editingPrize, id: editingPrizeId } : p
    ));
    setEditingPrizeId(null);
    setEditingPrize({});
  };

  // --- Segna come ritirato ---
  const handleMarkAsClaimed = async (winId: string) => {
    await updateDoc(doc(db, 'scratch_coupons', winId), { ritirato: true });
    setPendingWins(pendingWins.filter(w => w.id !== winId));
  };

  // Funzioni esistenti
  const handleEditPromo = (promo: any) => {
    setEditingPromoId(promo.id);
    setEditingPromoText(promo.testo);
    setEditingPromoImage(promo.image || '');
  };

  const handleSavePromo = async () => {
    if (!editingPromoId) return;
    await updateDoc(doc(db, 'promo', editingPromoId), {
      testo: editingPromoText,
      image: editingPromoImage
    });
    setPromoList(promoList.map(p =>
      p.id === editingPromoId ? { ...p, testo: editingPromoText, image: editingPromoImage } : p
    ));
    setEditingPromoId(null);
    setEditingPromoText('');
    setEditingPromoImage('');
  };

  const handleDeletePromo = async (id: string) => {
    await deleteDoc(doc(db, 'promo', id));
    setPromoList(promoList.filter(p => p.id !== id));
  };

  const handleEditVolantino = (volantino: any) => {
    setEditingVolantinoId(volantino.id);
    setEditingVolantino({ ...volantino });
  };

  const handleSaveVolantino = async () => {
    if (!editingVolantinoId) return;
    await updateDoc(doc(db, 'volantini', editingVolantinoId), editingVolantino);
    setVolantiniList(volantiniList.map(v =>
      v.id === editingVolantinoId ? { ...editingVolantino, id: editingVolantinoId } : v
    ));
    setEditingVolantinoId(null);
    setEditingVolantino({});
  };

  const handleDeleteVolantino = async (id: string) => {
    await deleteDoc(doc(db, 'volantini', id));
    setVolantiniList(volantiniList.filter(v => v.id !== id));
  };

  const handleLogout = async () => {
    await signOut(auth);
    window.location.reload();
  };

  const handleDeleteCliente = async (id: string) => {
    await deleteDoc(doc(db, 'utenti', id));
    setClienti(clienti.filter(c => c.id !== id));
  };

  const handleEditCliente = (cliente: any) => {
    setEditingClienteId(cliente.id);
    setEditingCliente({ ...cliente });
  };

  const handleSaveCliente = async () => {
    if (!editingClienteId) return;
    await updateDoc(doc(db, 'utenti', editingClienteId), editingCliente);
    setClienti(clienti.map(c =>
      c.id === editingClienteId ? { ...editingCliente, id: editingClienteId } : c
    ));
    setEditingClienteId(null);
    setEditingCliente({});
  };

  const clientiFiltrati = clienti
    .filter(c => !c.admin)
    .filter(c =>
      (c.nome + ' ' + c.cognome + ' ' + c.email + ' ' + c.cellulare + ' ' + c.barcode)
        .toLowerCase()
        .includes(searchCliente.toLowerCase())
    );

  const filteredWins = pendingWins.filter(win => {
    const cliente = clientiMap[win.userId];
    const testo = [
      win.prizeName,
      cliente?.nome || '',
      cliente?.cognome || '',
      cliente?.cellulare || ''
    ].join(' ').toLowerCase();
    return testo.includes(searchWin.toLowerCase());
  });

  const handleCloseAlert = () => {
    setAlertMessage(null);
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e3eafc 0%, #f5f7fa 100%)',
      pb: 6
    }}>
      {/* Header fisso */}
      <AppBar position="sticky" elevation={0} sx={{ 
        background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)', 
        mb: 4,
        boxShadow: '0 4px 20px rgba(30, 60, 114, 0.15)'
      }}>
        <Toolbar sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          py: 1.5,
          flexWrap: 'wrap',
          gap: { xs: 2, sm: 3 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2, md: 3 } }}>
            <LogoHeader />
            <Typography variant="h4" sx={{ 
              fontWeight: 800, 
              letterSpacing: 1.5, 
              color: '#fff',
              fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2.125rem' }
            }}>
              Gestione Backend
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={handleLogout} 
            sx={{ 
              fontWeight: 700, 
              fontSize: { xs: 12, sm: 14, md: 16 }, 
              px: { xs: 2, sm: 3, md: 4 }, 
              py: { xs: 0.8, md: 1.2 },
              boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)',
              '&:hover': { boxShadow: '0 6px 16px rgba(255, 255, 255, 0.3)' }
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Notifica alert */}
      {alertMessage && (
        <Snackbar 
          open={true} 
          autoHideDuration={6000} 
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseAlert} 
            severity={alertMessage.severity} 
            sx={{ width: '100%' }}
          >
            {alertMessage.message}
          </Alert>
        </Snackbar>
      )}

      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 1.5, sm: 2, md: 3, lg: 4 } }}>
        {/* PROMO */}
        <Paper sx={{
          p: { xs: 2, sm: 2.5, md: 4 },
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 4px 16px rgba(42, 82, 152, 0.12)',
          background: 'linear-gradient(135deg, #fff 60%, #e3eafc 100%)',
          overflow: 'hidden',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 6px 24px rgba(42, 82, 152, 0.15)'
          }
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: '#2a5298', 
            mb: 2.5, 
            letterSpacing: 0.5,
            fontSize: { xs: '1rem', md: '1.25rem' }
          }}>
            Promo in Corso
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Testo promo"
              value={promoText}
              onChange={e => setPromoText(e.target.value)}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Link immagine promo (opzionale)"
                value={promoImageUrl}
                onChange={e => setPromoImageUrl(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                placeholder="https://..."
                disabled={!!selectedPromoFile}
              />
              
              <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                oppure
              </Typography>
              
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                id="promo-image-upload"
                onChange={handlePromoFileChange}
                ref={promoFileInputRef}
              />
              <label htmlFor="promo-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImageIcon />}
                  disabled={!!promoImageUrl.trim() || uploadingPromo}
                >
                  Scegli immagine
                </Button>
              </label>
            </Box>
            
            {selectedPromoFile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="primary">
                  {selectedPromoFile.name} ({Math.round(selectedPromoFile.size / 1024)} KB)
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    setSelectedPromoFile(null);
                    if (promoFileInputRef.current) promoFileInputRef.current.value = '';
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleAddPromoWithUpload}
                sx={{ fontWeight: 600, px: 3, py: 1 }}
                disabled={uploadingPromo || !promoText.trim() || 
                  (!(promoImageUrl.trim() || selectedPromoFile))}
                startIcon={uploadingPromo ? <CircularProgress size={20} color="inherit" /> : <FileUploadIcon />}
              >
                {uploadingPromo ? 'Caricamento...' : 'Aggiungi promo'}
              </Button>
            </Box>
          </Box>
          
          <Divider sx={{ my: 3.5, opacity: 0.5 }} />

          <Box> 
            {promoList.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nessuna promo attiva.
              </Typography>
            )}
            {promoList.map((promo) => (
              <Paper
                key={promo.id}
                sx={{
                  p: 2.5,
                  px: 3,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  background: '#f5f7fa',
                  borderRadius: 2.5,
                  boxShadow: '0 2px 8px rgba(42, 82, 152, 0.08)',
                  mb: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': { 
                    boxShadow: '0 4px 16px rgba(42, 82, 152, 0.12)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {editingPromoId === promo.id ? (
                  <>
                    <TextField
                      label="Testo promo"
                      value={editingPromoText}
                      onChange={e => setEditingPromoText(e.target.value)}
                      size="small"
                      sx={{ minWidth: 120, flex: 1, '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                    />
                    <TextField
                      label="Link immagine"
                      value={editingPromoImage}
                      onChange={e => setEditingPromoImage(e.target.value)}
                      size="small"
                      sx={{ minWidth: 120, flex: 2, '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                    />
                    <Button variant="contained" color="success" onClick={handleSavePromo} sx={{ fontWeight: 700, px: 2.5 }}>Salva</Button>
                    <Button variant="outlined" color="inherit" onClick={() => setEditingPromoId(null)} sx={{ fontWeight: 600, px: 2.5 }}>Annulla</Button>
                  </>
                ) : (
                  <>
                    {promo.image && (
                      <img src={promo.image} alt="promo" style={{
                        width: 70, height: 70, objectFit: 'cover',
                        borderRadius: 12, boxShadow: '0 2px 12px #b6c6e6'
                      }} />
                    )}
                    <Typography variant="body1" sx={{ flex: 1, fontWeight: 500, color: '#1e3c72' }}>
                      {promo.testo}
                    </Typography>
                    <IconButton color="primary" onClick={() => handleEditPromo(promo)} size="small" title="Modifica">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDeletePromo(promo.id)} size="small" title="Elimina">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Paper>
            ))}
          </Box>
        </Paper>

        {/* VOLANTINI */}
        <Paper sx={{
          p: { xs: 2, sm: 2.5, md: 4 },
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 4px 16px rgba(42, 82, 152, 0.12)',
          background: 'linear-gradient(135deg, #fff 60%, #e3eafc 100%)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 6px 24px rgba(42, 82, 152, 0.15)'
          }
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: '#2a5298', 
            mb: 2.5, 
            letterSpacing: 0.5,
            fontSize: { xs: '1rem', md: '1.25rem' }
          }}>
            Volantini (PDF)
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Nome volantino"
              value={volantinoName}
              onChange={e => setVolantinoName(e.target.value)}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Link PDF volantino (opzionale)"
                value={volantinoUrl}
                onChange={e => setVolantinoUrl(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                placeholder="https://..."
                disabled={!!selectedVolantinoFile}
              />
              
              <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                oppure
              </Typography>
              
              <input
                type="file"
                accept=".pdf"
                style={{ display: 'none' }}
                id="volantino-pdf-upload"
                onChange={handleVolantinoFileChange}
                ref={volantinoFileInputRef}
              />
              <label htmlFor="volantino-pdf-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PictureAsPdfIcon />}
                  disabled={!!volantinoUrl.trim() || uploadingVolantino}
                >
                  Scegli PDF
                </Button>
              </label>
            </Box>
            
            {selectedVolantinoFile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PictureAsPdfIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="primary">
                  {selectedVolantinoFile.name} ({Math.round(selectedVolantinoFile.size / 1024)} KB)
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    setSelectedVolantinoFile(null);
                    if (volantinoFileInputRef.current) volantinoFileInputRef.current.value = '';
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleAddVolantinoWithUpload}
                sx={{ fontWeight: 600, px: 3, py: 1 }}
                disabled={uploadingVolantino || !volantinoName.trim() || 
                  (!(volantinoUrl.trim() || selectedVolantinoFile))}
                startIcon={uploadingVolantino ? <CircularProgress size={20} color="inherit" /> : <FileUploadIcon />}
              >
                {uploadingVolantino ? 'Caricamento...' : 'Aggiungi volantino'}
              </Button>
            </Box>
          </Box>
          
          <Divider sx={{ my: 3.5, opacity: 0.5 }} />

          <Box>
            {volantiniList.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nessun volantino caricato.
              </Typography>
            )}
            {volantiniList.map((v) => (
              <Paper
                key={v.id}
                sx={{
                  p: 2.5,
                  px: 3,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  background: '#f5f7fa',
                  borderRadius: 2.5,
                  boxShadow: '0 2px 8px rgba(42, 82, 152, 0.08)',
                  mb: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': { 
                    boxShadow: '0 4px 16px rgba(42, 82, 152, 0.12)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {editingVolantinoId === v.id ? (
                  <>
                    <TextField
                      label="Nome volantino"
                      value={editingVolantino.nome}
                      onChange={e => setEditingVolantino({ ...editingVolantino, nome: e.target.value })}
                      size="small"
                      sx={{ minWidth: 120, flex: 1, '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                    />
                    <TextField
                      label="Link PDF"
                      value={editingVolantino.url}
                      onChange={e => setEditingVolantino({ ...editingVolantino, url: e.target.value })}
                      size="small"
                      sx={{ minWidth: 120, flex: 2, '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                    />
                    <IconButton color="success" size="small" onClick={handleSaveVolantino} title="Salva">
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton color="inherit" size="small" onClick={() => setEditingVolantinoId(null)} title="Annulla">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: 24, minWidth: 24 }} />
                    <Typography variant="body1" sx={{ flex: 1, fontWeight: 500, color: '#1e3c72' }}>
                      {v.nome}
                    </Typography>
                    <Button 
                      size="small"
                      variant="text"
                      href={v.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ fontWeight: 600, textTransform: 'none', fontSize: '0.875rem' }}
                    >
                      Visualizza PDF
                    </Button>
                    <IconButton color="primary" onClick={() => handleEditVolantino(v)} size="small" title="Modifica">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDeleteVolantino(v.id)} size="small" title="Elimina">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Paper>
            ))}
          </Box>
        </Paper>

        {/* PREMI GRATTA E VINCI */}
        <Paper sx={{
          p: { xs: 2, sm: 2.5, md: 4 },
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 4px 16px rgba(42, 82, 152, 0.12)',
          background: 'linear-gradient(135deg, #fff 60%, #e3eafc 100%)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 6px 24px rgba(42, 82, 152, 0.15)'
          }
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: '#2a5298', 
            mb: 2.5, 
            letterSpacing: 0.5,
            fontSize: { xs: '1rem', md: '1.25rem' }
          }}>
            Premi Gratta e Vinci
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Nome premio"
              value={prizeName}
              onChange={e => setPrizeName(e.target.value)}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }
              }}
              disabled={scratchPrizes.length >= 3}
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                label="Link immagine premio (opzionale)"
                value={prizeImageUrl}
                onChange={e => setPrizeImageUrl(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                placeholder="https://..."
                disabled={!!selectedPrizeFile || scratchPrizes.length >= 3}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                oppure
              </Typography>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                id="prize-image-upload"
                onChange={handlePrizeFileChange}
                ref={prizeFileInputRef}
                disabled={scratchPrizes.length >= 3}
              />
              <label htmlFor="prize-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImageIcon />}
                  disabled={!!prizeImageUrl.trim() || uploadingPrize || scratchPrizes.length >= 3}
                >
                  Scegli immagine
                </Button>
              </label>
            </Box>
            {selectedPrizeFile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="primary">
                  {selectedPrizeFile.name} ({Math.round(selectedPrizeFile.size / 1024)} KB)
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedPrizeFile(null);
                    if (prizeFileInputRef.current) prizeFileInputRef.current.value = '';
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleAddPrizeWithUpload}
                sx={{ fontWeight: 700, px: 3, py: 1.2 }}
                disabled={
                  uploadingPrize ||
                  !prizeName.trim() ||
                  (!(prizeImageUrl.trim() || selectedPrizeFile)) ||
                  scratchPrizes.length >= 3
                }
                startIcon={uploadingPrize ? <CircularProgress size={20} color="inherit" /> : <FileUploadIcon />}
              >
                {uploadingPrize ? 'Caricamento...' : 'Aggiungi premio'}
              </Button>
            </Box>
          </Box>
          <Divider sx={{ my: 3.5, opacity: 0.5 }} />
          <Box>
            {scratchPrizes.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nessun premio inserito.
              </Typography>
            )}
            {scratchPrizes.map((prize) => (
              <Paper
                key={prize.id}
                sx={{
                  p: 2.5,
                  px: 3,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  background: '#f5f7fa',
                  borderRadius: 2.5,
                  boxShadow: '0 2px 8px rgba(42, 82, 152, 0.08)',
                  mb: 2,
                  overflow: 'hidden',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': { 
                    boxShadow: '0 4px 16px rgba(42, 82, 152, 0.12)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {editingPrizeId === prize.id ? (
                  <>
                    <TextField
                      label="Nome premio"
                      value={editingPrize.nome}
                      onChange={e => setEditingPrize({ ...editingPrize, nome: e.target.value })}
                      size="small"
                      sx={{ minWidth: 120, flex: 1, '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                    />
                    <TextField
                      label="Link immagine"
                      value={editingPrize.img}
                      onChange={e => setEditingPrize({ ...editingPrize, img: e.target.value })}
                      size="small"
                      sx={{ minWidth: 120, flex: 2, '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                    />
                    <IconButton color="success" size="small" onClick={handleSavePrize} title="Salva">
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton color="inherit" size="small" onClick={() => setEditingPrizeId(null)} title="Annulla">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    {prize.img && (
                      <img 
                        src={prize.img} 
                        alt="premio" 
                        style={{
                          width: 70, 
                          height: 70, 
                          minWidth: 70, // Aggiunto questo
                          objectFit: 'cover',
                          borderRadius: 12, 
                          boxShadow: '0 2px 12px #b6c6e6'
                        }} 
                      />
                    )}
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        flex: 1, 
                        fontWeight: 500, 
                        color: '#1e3c72',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {prize.nome}
                    </Typography>
                    <IconButton color="primary" onClick={() => handleEditPrize(prize)} size="small" title="Modifica">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDeletePrize(prize.id)} size="small" title="Elimina">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Paper>
            ))}
          </Box>
        </Paper>

        {/* VINCITE GRATTA E VINCI */}
        <Paper sx={{
          p: { xs: 2, sm: 2.5, md: 4 },
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 4px 16px rgba(42, 82, 152, 0.12)',
          background: 'linear-gradient(135deg, #fff 60%, #e3eafc 100%)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 6px 24px rgba(42, 82, 152, 0.15)'
          }
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: '#2a5298', 
            mb: 2.5, 
            letterSpacing: 0.5,
            fontSize: { xs: '1rem', md: '1.25rem' }
          }}>
            Vincite Gratta e Vinci da ritirare
          </Typography>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
            <TextField
              label="Cerca vincita per nome o cellulare"
              value={searchWin}
              onChange={e => setSearchWin(e.target.value)}
              size="small"
              sx={{ 
                minWidth: { xs: '100%', sm: 300 }, 
                background: '#f5f7fa', 
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  '& fieldset': {
                    borderColor: '#d0d9f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#a4b7e0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2a5298',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', md: '1rem' },
                }
              }}
              InputProps={{
                endAdornment: searchWin && (
                  <IconButton size="small" onClick={() => setSearchWin('')} title="Pulisci ricerca">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )
              }}
            />
          </Box>
          {filteredWins.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nessuna vincita da ritirare.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredWins.map(win => {
                const cliente = clientiMap[win.userId];
                return (
                  <Paper key={win.id} sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    mb: 2,
                    background: 'linear-gradient(90deg, #f5f7fa 70%, #e3eafc 100%)',
                    borderRadius: 2.5,
                    boxShadow: '0 2px 8px rgba(42, 82, 152, 0.08)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 16px rgba(42, 82, 152, 0.12)'
                    }
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ 
                        fontWeight: 700, 
                        color: '#1e3c72', 
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        mb: 1 
                      }}>
                        {win.prizeName}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#555', mb: 0.5 }}>
                        Cliente: <b>{cliente ? `${cliente.nome} ${cliente.cognome}` : win.userId}</b>
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#555', mb: 0.5 }}>
                        Cellulare: <b>{cliente?.cellulare || '-'}</b>
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Data vincita: {win.date ? new Date(win.date).toLocaleString() : '-'}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleMarkAsClaimed(win.id)}
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        px: 2.5, 
                        py: 1.2,
                        borderRadius: 2,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
                        '&:hover': { boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)' }
                      }}
                    >
                      Segna come ritirato
                    </Button>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Paper>

        {/* CLIENTI - Mantenuto come prima */}
        <Paper sx={{
          p: { xs: 2, sm: 2.5, md: 4 },
          borderRadius: 3,
          boxShadow: '0 4px 16px rgba(42, 82, 152, 0.12)',
          background: 'linear-gradient(135deg, #fff 60%, #e3eafc 100%)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 6px 24px rgba(42, 82, 152, 0.15)'
          }
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: '#2a5298', 
            mb: 2.5, 
            letterSpacing: 0.5,
            fontSize: { xs: '1rem', md: '1.25rem' }
          }}>
            Clienti Registrati
          </Typography>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
            <TextField
              label="Cerca cliente"
              value={searchCliente}
              onChange={e => setSearchCliente(e.target.value)}
              size="small"
              sx={{ 
                minWidth: { xs: '100%', sm: 250 }, 
                background: '#f5f7fa', 
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }
              }}
            />
          </Box>
          <TableContainer sx={{ 
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(42, 82, 152, 0.08)', 
            background: '#f5f7fa',
            overflow: 'auto',
            maxHeight: '60vh',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#e3eafc',
              borderRadius: '10px'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#a4b7e0',
              borderRadius: '10px',
              '&:hover': { background: '#8fa5d8' }
            }
          }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(90deg, #e3eafc 0%, #d2ddfa 100%)' }}>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: '#1e3c72', 
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, 
                    py: 2,
                    minWidth: 120
                  }}>Codice cliente</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: '#1e3c72', 
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, 
                    py: 2,
                    minWidth: 90
                  }}>Nome</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: '#1e3c72', 
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, 
                    py: 2,
                    minWidth: 90
                  }}>Cognome</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: '#1e3c72', 
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, 
                    py: 2,
                    minWidth: 120
                  }}>Email</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: '#1e3c72', 
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, 
                    py: 2,
                    minWidth: 100
                  }}>Cellulare</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: '#1e3c72', 
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, 
                    py: 2,
                    minWidth: 110
                  }}>Barcode</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 800, 
                    color: '#1e3c72', 
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, 
                    py: 2,
                    minWidth: 80,
                    textAlign: 'center'
                  }}>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientiFiltrati.map(cliente => (
                  <TableRow key={cliente.id} sx={{
                    transition: 'background 0.15s ease-in-out',
                    '&:hover': { 
                      background: '#e3eafc',
                      transform: 'scale(1.01)',
                      transformOrigin: 'center'
                    },
                    borderBottom: '1px solid #e0e0e0',
                    fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }
                  }}>
                    {editingClienteId === cliente.id ? (
                      <>
                        <TableCell sx={{ py: 1.5, fontWeight: 500 }}>{cliente.codiceCliente}</TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <TextField
                            value={editingCliente.nome}
                            onChange={e => setEditingCliente({ ...editingCliente, nome: e.target.value })}
                            size="small"
                            fullWidth
                            sx={{ fontSize: '0.875rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <TextField
                            value={editingCliente.cognome}
                            onChange={e => setEditingCliente({ ...editingCliente, cognome: e.target.value })}
                            size="small"
                            fullWidth
                            sx={{ fontSize: '0.875rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <TextField
                            value={editingCliente.email}
                            onChange={e => setEditingCliente({ ...editingCliente, email: e.target.value })}
                            size="small"
                            fullWidth
                            sx={{ fontSize: '0.875rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <TextField
                            value={editingCliente.cellulare}
                            onChange={e => setEditingCliente({ ...editingCliente, cellulare: e.target.value })}
                            size="small"
                            fullWidth
                            sx={{ fontSize: '0.875rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5, fontWeight: 500 }}>
                          {cliente.barcode}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <IconButton color="success" size="small" onClick={handleSaveCliente} title="Salva">
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton color="inherit" size="small" onClick={() => setEditingClienteId(null)} title="Annulla">
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ py: 1.5, fontWeight: 500 }}>{cliente.codiceCliente}</TableCell>
                        <TableCell sx={{ py: 1.5 }}>{cliente.nome}</TableCell>
                        <TableCell sx={{ py: 1.5 }}>{cliente.cognome}</TableCell>
                        <TableCell sx={{ py: 1.5, wordBreak: 'break-word' }}>{cliente.email}</TableCell>
                        <TableCell sx={{ py: 1.5 }}>{cliente.cellulare}</TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ wordBreak: 'break-all', fontSize: '0.8rem' }}>
                            {cliente.barcode}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <IconButton color="primary" size="small" onClick={() => handleEditCliente(cliente)} title="Modifica">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton color="error" size="small" onClick={() => handleDeleteCliente(cliente.id)} title="Elimina">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
}